import { useEffect, useState, type ChangeEvent } from 'react';
import { Calendar, Clock, Phone } from 'lucide-react';
import type { Cita, Cliente, NuevaCitaDraft } from '../../types';
import { BuscadorCliente } from '../clientes/BuscadorCliente';

interface Props {
  editando: Cita | null;
  clientePrecargado: Cliente | null;
  buscarClientes: (texto: string) => Cliente[];
  onGuardar: (
    draft: NuevaCitaDraft,
    nombreCliente: string,
    clienteSeleccionado: Cliente | null
  ) => void;
  onCancelar: () => void;
  onSolicitarNuevoCliente: (nombre: string) => void;
}

export function FormularioCita({
  editando,
  clientePrecargado,
  buscarClientes,
  onGuardar,
  onCancelar,
  onSolicitarNuevoCliente,
}: Props) {
  const [draft, setDraft] = useState<NuevaCitaDraft>({
    telefono: editando?.telefono ?? '',
    fecha: editando?.fecha ?? '',
    hora: editando?.hora ?? '',
    notas: editando?.notas ?? '',
  });
  const [busqueda, setBusqueda] = useState(editando?.cliente ?? '');
  const [clienteSeleccionado, setClienteSeleccionado] = useState<Cliente | null>(null);
  const [resultados, setResultados] = useState<Cliente[]>([]);

  useEffect(() => {
    setResultados(buscarClientes(busqueda));
  }, [busqueda, buscarClientes]);

  useEffect(() => {
    if (clientePrecargado) {
      setClienteSeleccionado(clientePrecargado);
      setBusqueda(clientePrecargado.nombre);
      setDraft((d) => ({ ...d, telefono: clientePrecargado.telefono || '' }));
      setResultados([]);
    }
  }, [clientePrecargado]);

  const onCambioBusqueda = (texto: string) => {
    setBusqueda(texto);
    setClienteSeleccionado(null);
  };

  const onSeleccionarCliente = (cliente: Cliente) => {
    setClienteSeleccionado(cliente);
    setBusqueda(cliente.nombre);
    setDraft({ ...draft, telefono: cliente.telefono || '' });
    setResultados([]);
  };

  const guardar = () => {
    if (!draft.fecha || !draft.hora) {
      alert('Por favor completa fecha y hora');
      return;
    }
    // cliente y telefono son opcionales: una cita puede ser un recordatorio
    // suelto o un cliente unico que el usuario no quiere registrar.
    onGuardar(draft, busqueda, clienteSeleccionado);
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
      <h2 className="text-xl font-bold text-gray-800 mb-4">
        {editando ? '✏️ Editar Cita' : 'Nueva Cita'}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <BuscadorCliente
          valor={busqueda}
          resultados={resultados}
          clienteSeleccionado={clienteSeleccionado}
          onCambioTexto={onCambioBusqueda}
          onSeleccionar={onSeleccionarCliente}
          onSolicitarNuevo={() => onSolicitarNuevoCliente(busqueda)}
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Phone size={16} className="inline mr-1" />
            Teléfono
          </label>
          <input
            type="tel"
            value={draft.telefono}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setDraft({ ...draft, telefono: e.target.value })
            }
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            placeholder="Teléfono del cliente"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Calendar size={16} className="inline mr-1" />
            Fecha *
          </label>
          <input
            type="date"
            value={draft.fecha}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setDraft({ ...draft, fecha: e.target.value })
            }
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Clock size={16} className="inline mr-1" />
            Hora *
          </label>
          <input
            type="time"
            value={draft.hora}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setDraft({ ...draft, hora: e.target.value })
            }
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">Notas</label>
          <textarea
            value={draft.notas}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
              setDraft({ ...draft, notas: e.target.value })
            }
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            rows={2}
            placeholder="Detalles adicionales..."
          />
        </div>
      </div>
      <div className="flex gap-3 mt-6">
        <button
          onClick={guardar}
          className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
        >
          {editando ? 'Guardar Cambios' : 'Guardar Cita'}
        </button>
        <button
          onClick={onCancelar}
          className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}
